import {
  AlertOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BgColorsOutlined,
  BookOutlined,
  BoxPlotOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  ClusterOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DesktopOutlined,
  EditOutlined,
  EyeOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  GlobalOutlined,
  HistoryOutlined,
  HomeOutlined,
  IdcardOutlined,
  LockOutlined,
  NodeIndexOutlined,
  PictureOutlined,
  PieChartOutlined,
  PlayCircleOutlined,
  ProductOutlined,
  RiseOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShareAltOutlined,
  SlidersOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  VideoCameraOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import type { ItemType } from 'antd/es/menu/interface';
import { type FC, type LazyExoticComponent, lazy } from 'react';
import type { ItemMenu, MenuResponse } from '@/domain/menu.ts';

const iconMap = {
  SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  ApartmentOutlined: <ApartmentOutlined />,
  TeamOutlined: <TeamOutlined />,
  PieChartOutlined: <PieChartOutlined />,
  DeleteOutlined: <DeleteOutlined />,
  DesktopOutlined: <DesktopOutlined />,
  ShareAltOutlined: <ShareAltOutlined />,
  BgColorsOutlined: <BgColorsOutlined />,
  GlobalOutlined: <GlobalOutlined />,
  FileOutlined: <FileOutlined />,
  LockOutlined: <LockOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  IdcardOutlined: <IdcardOutlined />,
  WifiOutlined: <WifiOutlined />,
  HistoryOutlined: <HistoryOutlined />,
  UploadOutlined: <UploadOutlined />,
  UnorderedListOutlined: <UnorderedListOutlined />,
  EyeOutlined: <EyeOutlined />,
  EditOutlined: <EditOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  BookOutlined: <BookOutlined />,
  BarChartOutlined: <BarChartOutlined />,
  VideoCameraOutlined: <VideoCameraOutlined />,
  ClusterOutlined: <ClusterOutlined />,
  RobotOutlined: <RobotOutlined />,
  SettingOutlined: <SettingOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  CloudOutlined: <CloudOutlined />,
  PlayCircleOutlined: <PlayCircleOutlined />,
  HomeOutlined: <HomeOutlined />,
  ProductOutlined: <ProductOutlined />,
  SlidersOutlined: <SlidersOutlined />,
  BoxPlotOutlined: <BoxPlotOutlined />,
  FolderOpenOutlined: <FolderOpenOutlined />,
  FilePdfOutlined: <FilePdfOutlined />,
  FileImageOutlined: <FileImageOutlined />,
  RiseOutlined: <RiseOutlined />,
  PictureOutlined: <PictureOutlined />,
  NodeIndexOutlined: <NodeIndexOutlined />,
  AlertOutlined: <AlertOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
};

const lazyMap = new Map<string, LazyExoticComponent<FC>>([
  ['Users', lazy(() => import('@/app/Users')) as LazyExoticComponent<FC>],
  ['ControlPanel', lazy(() => import('@/app/ControlPanel')) as LazyExoticComponent<FC>],
  ['Dashboard', lazy(() => import('@/app/Dashboard')) as LazyExoticComponent<FC>],
  ['ProjectList', lazy(() => import('@/app/ProjectList')) as LazyExoticComponent<FC>],
  ['MultiViewer', lazy(() => import('@/app/MultiViewer')) as LazyExoticComponent<FC>],
  ['ProjectList/PlayVideo', lazy(() => import('@/app/ProjectList/PlayVideo')) as LazyExoticComponent<FC>],
  ['ProjectList/PlayImage', lazy(() => import('@/app/ProjectList/PlayImage')) as LazyExoticComponent<FC>],
  ['VideoList/PlayVideo', lazy(() => import('@/app/VideoList/PlayVideo')) as LazyExoticComponent<FC>],
  ['Media', lazy(() => import('@/app/Media')) as LazyExoticComponent<FC>],
  ['Schedules', lazy(() => import('@/app/Schedules')) as LazyExoticComponent<FC>],
  ['Program', lazy(() => import('@/app/Program')) as LazyExoticComponent<FC>],
  ['Process', lazy(() => import('@/app/Process')) as LazyExoticComponent<FC>],
  ['DashboardCliente', lazy(() => import('@/app/ControlPanel/ControlPanel')) as LazyExoticComponent<FC>],
  ['UploadFiles', lazy(() => import('@/app/UploadFiles')) as LazyExoticComponent<FC>],
  ['SocialNetworkPublication', lazy(() => import('@/app/SocialNetworkPublication')) as LazyExoticComponent<FC>],
  ['SocialNetworkAnalitic', lazy(() => import('@/app/SocialNetworkAnalitic')) as LazyExoticComponent<FC>],
  ['SignalAudit', lazy(() => import('@/app/SignalAudit')) as LazyExoticComponent<FC>],
  ['ExternalNotesList', lazy(() => import('@/app/ExternalNotesList')) as LazyExoticComponent<FC>],
  ['ExternalNotesList/PlayMedia', lazy(() => import('@/app/ExternalNotesList/PlayMedia')) as LazyExoticComponent<FC>],
  ['Themes', lazy(() => import('@/app/Themes')) as LazyExoticComponent<FC>],
  ['MultimediaEditor', lazy(() => import('@/app/MultimediaEditor')) as LazyExoticComponent<FC>],
  ['Profile', lazy(() => import('@/app/Profile')) as LazyExoticComponent<FC>],
  ['Permissions', lazy(() => import('@/app/Permissions')) as LazyExoticComponent<FC>],
  ['ai_models', lazy(() => import('@/app/AIModels')) as LazyExoticComponent<FC>],
  ['Comercial', lazy(() => import('@/app/Comercial')) as LazyExoticComponent<FC>],
  ['Object', lazy(() => import('@/app/AlertCenter/Object')) as LazyExoticComponent<FC>],
  ['Settings', lazy(() => import('@/app/AlertCenter/Settings')) as LazyExoticComponent<FC>],
  ['Services', lazy(() => import('@/app/Services')) as LazyExoticComponent<FC>],
  ['CmsPublicationPage', lazy(() => import('@/app/CmsPublication')) as LazyExoticComponent<FC>],
  ['WorkflowManagement', lazy(() => import('@/app/WorkflowManagement')) as LazyExoticComponent<FC>],
  ['Events', lazy(() => import('@/app/AlertCenter/Events')) as LazyExoticComponent<FC>],
  ['Fields', lazy(() => import('@/app/Fields')) as LazyExoticComponent<FC>],
  ['Tokens', lazy(() => import('@/app/Tokens')) as LazyExoticComponent<FC>],
  ['Secrets', lazy(() => import('@/app/Secrets')) as LazyExoticComponent<FC>],
  ['file_debug', lazy(() => import('@/app/FileDebug')) as LazyExoticComponent<FC>],
  ['Streaming', lazy(() => import('@/app/Streaming')) as LazyExoticComponent<FC>],
  ['CaptureApplications', lazy(() => import('@/app/CaptureApplications')) as LazyExoticComponent<FC>],
  ['multimedia_history', lazy(() => import('@/app/MultimediaHistory')) as LazyExoticComponent<FC>],
  ['ProjectList/EditorVideo', lazy(() => import('@/app/ProjectList/EditorVideo')) as LazyExoticComponent<FC>],
  [
    'ProjectList/EditorVideoKlippy',
    lazy(() => import('@/app/ProjectList/EditorVideo/EditorVideoKlippy')) as LazyExoticComponent<FC>,
  ],
  ['ProjectList/EditorImagen', lazy(() => import('@/app/ProjectList/EditorImagen')) as LazyExoticComponent<FC>],
  ['MultimediaEditor/EditorVideo', lazy(() => import('@/app/MultimediaEditor/EditorVideo')) as LazyExoticComponent<FC>],
  [
    'MultimediaEditor/EditorVideoKlippy',
    lazy(() => import('@/app/MultimediaEditor/EditorVideoKlippy')) as LazyExoticComponent<FC>,
  ],
  [
    'MultimediaEditor/EditorImagen',
    lazy(() => import('@/app/MultimediaEditor/EditorImagen')) as LazyExoticComponent<FC>,
  ],
  ['Rating', lazy(() => import('@/app/Rating')) as LazyExoticComponent<FC>],
  ['KPIAudiencia', lazy(() => import('@/app/KPIAudiencia')) as LazyExoticComponent<FC>],
  ['Session', lazy(() => import('@/app/Session')) as LazyExoticComponent<FC>],
  ['FileDebugging', lazy(() => import('@/app/FileDebugging')) as LazyExoticComponent<FC>],
  ['StreamingAuditApps', lazy(() => import('@/app/StreamingAuditApps')) as LazyExoticComponent<FC>],
  ['NodeConfigProcess', lazy(() => import('@/app/NodeConfigProcess')) as LazyExoticComponent<FC>],
  ['Menu', lazy(() => import('@/app/Menu')) as LazyExoticComponent<FC>],
  ['Keywords', lazy(() => import('@/app/Keywords')) as LazyExoticComponent<FC>],
  ['Company', lazy(() => import('@/app/Company')) as LazyExoticComponent<FC>],
  ['VideoList', lazy(() => import('@/app/VideoList')) as LazyExoticComponent<FC>],
  ['FaceCategory', lazy(() => import('@/app/Face/FaceCategoriesEntry')) as LazyExoticComponent<FC>],
  ['Face', lazy(() => import('@/app/Face')) as LazyExoticComponent<FC>],
  ['Face/VideoReview', lazy(() => import('@/app/Face/FaceVideoReviewEntry')) as LazyExoticComponent<FC>],
  ['Identity', lazy(() => import('@/app/Identity')) as LazyExoticComponent<FC>],
  ['TranscriptionVocabulary', lazy(() => import('@/app/TranscriptionVocabulary')) as LazyExoticComponent<FC>],
  ['TranscriptionAdmin', lazy(() => import('@/app/TranscriptionVocabulary')) as LazyExoticComponent<FC>],
  ['SpeechmaticsDictionary', lazy(() => import('@/app/SpeechmaticsDictionary')) as LazyExoticComponent<FC>],
]);

const defaultTabsPage: Record<string, ItemMenu> = {
  'ProjectList/PlayVideo': {
    key: 'ProjectList/PlayVideo',
    module: 'Home',
    label: 'Preview Video',
    icon: iconMap['PlayCircleOutlined'],
    router: 'ProjectList/PlayVideo',
    isMultiInstance: true,
    Component: lazyMap.get('ProjectList/PlayVideo'),
    children: [],
  },
  'VideoList/PlayVideo': {
    key: 'VideoList/PlayVideo',
    label: 'Preview Video List',
    icon: iconMap['PlayCircleOutlined'],
    router: 'VideoList/PlayVideo',
    isMultiInstance: true,
    Component: lazyMap.get('VideoList/PlayVideo'),
    children: [],
  },
  'Face/VideoReview': {
    key: 'Face/VideoReview',
    label: 'Revisión de rostros',
    icon: iconMap['IdcardOutlined'],
    router: 'Face/VideoReview',
    isMultiInstance: true,
    Component: lazyMap.get('Face/VideoReview'),
    children: [],
  },
  'ProjectList/PlayImage': {
    key: 'ProjectList/PlayImage',
    label: 'Preview Image',
    icon: iconMap['PlayCircleOutlined'],
    router: 'ProjectList/PlayImage',
    isMultiInstance: true,
    Component: lazyMap.get('ProjectList/PlayImage'),
    children: [],
  },
  'ExternalNotesList/PlayMedia': {
    key: 'ExternalNotesList/PlayMedia',
    label: 'Preview Media',
    icon: iconMap['PlayCircleOutlined'],
    router: 'ExternalNotesList/PlayMedia',
    isMultiInstance: true,
    Component: lazyMap.get('ExternalNotesList/PlayMedia'),
    children: [],
  },
  'ProjectList/EditorVideo': {
    key: 'ProjectList/EditorVideo',
    label: 'Editor de video',
    icon: iconMap['VideoCameraOutlined'],
    router: 'ProjectList/EditorVideo',
    isMultiInstance: true,
    Component: lazyMap.get('ProjectList/EditorVideo'),
    children: [],
  },
  'ProjectList/EditorVideoKlippy': {
    key: 'ProjectList/EditorVideoKlippy',
    label: 'Editor de video',
    icon: iconMap['VideoCameraOutlined'],
    router: 'ProjectList/EditorVideoKlippy',
    isMultiInstance: true,
    Component: lazyMap.get('ProjectList/EditorVideoKlippy'),
    children: [],
  },
  'ProjectList/EditorImagen': {
    key: 'ProjectList/EditorImagen',
    label: 'Editor Imagen',
    icon: iconMap['VideoCameraOutlined'],
    router: 'ProjectList/EditorImagen',
    isMultiInstance: true,
    Component: lazyMap.get('ProjectList/EditorImagen'),
    children: [],
  },
  'MultimediaEditor/EditorVideo': {
    key: 'MultimediaEditor/EditorVideo',
    label: 'Plantillas de video',
    icon: iconMap['VideoCameraOutlined'],
    router: 'MultimediaEditor/EditorVideo',
    isMultiInstance: true,
    Component: lazyMap.get('MultimediaEditor/EditorVideo'),
    children: [],
  },
  'MultimediaEditor/EditorVideoKlippy': {
    key: 'MultimediaEditor/EditorVideoKlippy',
    label: 'Plantillas de video',
    icon: iconMap['VideoCameraOutlined'],
    router: 'MultimediaEditor/EditorVideoKlippy',
    isMultiInstance: true,
    Component: lazyMap.get('MultimediaEditor/EditorVideoKlippy'),
    children: [],
  },
  'MultimediaEditor/EditorImagen': {
    key: 'MultimediaEditor/EditorImagen',
    label: 'Editor Plantilla Imagen',
    icon: iconMap['VideoCameraOutlined'],
    router: 'MultimediaEditor/EditorImagen',
    isMultiInstance: true,
    Component: lazyMap.get('MultimediaEditor/EditorImagen'),
    children: [],
  },
};

export const filterTabPageMap = (menus: MenuResponse[], parentModule?: string) => {
  return menus.reduce<Record<string, ItemMenu>>((map, item) => {
    const currentModule = parentModule ?? item.label;
    if (item?.router) {
      map[item!.router] = {
        ...item,
        key: item.router ? item.router : item.key,
        module: currentModule,
        icon: item.icon in iconMap ? iconMap[item.icon as keyof typeof iconMap] : undefined,
        Component: lazyMap.get(item.router),
      };
    }

    if (item.children) {
      Object.assign(map, filterTabPageMap(item?.children, currentModule));
    }
    return map;
  }, defaultTabsPage);
};

export const getTransformToMenu = (menus: MenuResponse[]): ItemType[] => {
  return menus.map(({ label, ...menu }) => ({
    label,
    key: menu.router ? menu.router : menu.key,
    icon: menu.icon in iconMap ? iconMap[menu.icon as keyof typeof iconMap] : undefined,
    children: menu.children ? getTransformToMenu(menu.children) : undefined,
  }));
};
